<template>
	<div class="add-worker-modal">
		<app-loader
			v-if="isLoading"
			:style="loaderStyle"
		/>
		<div class="add-worker-modal__search">
			<app-input
				ref="$input"
				v-model="filter"
				class="add-worker-modal__search-input"
				:placeholder="$t('search_short')"
			/>
			<div
				v-if="props.showCloseIcon"
				class="add-worker-modal__close"
			>
				<app-tooltip :text="$t('close_label')">
					<template #activator>
						<app-icon
							name="close"
							width="20px"
							color="#D2D2D2"
							hover-color="#499259"
							cursor="pointer"
							@click="emit('close')"
						/>
					</template>
				</app-tooltip>
			</div>
		</div>
		<div class="add-worker-modal__header">
			<div class="add-worker-modal__header-title">
				{{ $t('client_workers') }}
			</div>
		</div>
		<div
			class="add-worker-modal__body"
			ref="$body"
		>
			<template
				v-for="(holder, idx) in filteredHolders"
				:key="idx"
			>
				<div
					v-if="holder.IsWorker"
					class="add-worker-modal__body-item"
				>
					<app-checkbox
						:style="{ height: '16px', marginRight: '8px' }"
						:value="isWorkerAdded(holder.Worker.Id)"
						text=""
						@input="onChangeWorkerStage($event, holder.Worker.Id)"
					/>
					<div
						class="add-worker-modal__body-item-text"
						@click="onChangeWorkerStageFromName(holder.Worker.Id)"
					>
						{{ holder.Worker.Name }}
					</div>
				</div>
				<div
					v-else
					class="add-worker-modal__body-department"
				>
					<div class="add-worker-modal__body-department-title">
						{{ holder.Departament.Name }}
					</div>
					<div
						v-for="worker in holder.Departament.Workers"
						:key="worker.Id"
						class="add-worker-modal__body-item add-worker-modal__body-item--nested"
					>
						<app-checkbox
							:style="{ height: '16px', marginRight: '8px' }"
							:value="isWorkerAdded(worker.Id)"
							text=""
							@input="onChangeWorkerStage($event, worker.Id)"
						/>
						<div
							class="add-worker-modal__body-item-text"
							@click="onChangeWorkerStageFromName(worker.Id)"
						>
							{{ worker.Name }}
						</div>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { ProjectApi } from "@/ api/ProjectApi";
import { CommonWorkerHolder } from "@/models/interfaces/workers/worker-models";
import { projectPageStore } from "@/store/project-page";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

const props = defineProps<{
	projectId: number,
	addedWorkers: { Id: number, Name: string }[],
	showCloseIcon?: boolean,
}>()

const emit = defineEmits(['close'])

const $input = ref<typeof AppInput | null>(null)
const $body = ref<HTMLDivElement | null>(null)

const store = projectPageStore()

const isLoading = ref(true)
const filter = ref('')
const holders = ref<CommonWorkerHolder[]>([])

const addedIds = computed(() => new Set((props.addedWorkers || []).map(w => w.Id)))

const filteredHolders = computed<CommonWorkerHolder[]>(() => {
	const query = filter.value.trim().toLowerCase()
	if (!query) return holders.value
	const result: CommonWorkerHolder[] = []
	for (const holder of holders.value) {
		if (holder.IsWorker) {
			if (holder.Worker?.Name?.toLowerCase().includes(query)) {
				result.push(holder)
			}
		} else {
			const dept = holder.Departament
			if (!dept) continue
			if (dept.Name?.toLowerCase().includes(query)) {
				result.push(holder)
			} else {
				const matched = (dept.Workers || []).filter(
					w => w.Name?.toLowerCase().includes(query)
				)
				if (matched.length) {
					result.push({
						...holder,
						Departament: { ...dept, Workers: matched }
					})
				}
			}
		}
	}
	return result
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const onFetchWorkers = async () => {
	try {
		isLoading.value = true
		holders.value = await ProjectApi.getAttachWorkersList(props.projectId)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const isWorkerAdded = (workerId: number) => addedIds.value.has(workerId)

const onAttach = async (workerId: number) => {
	try {
		isLoading.value = true
		await store.attachWorkerToProject(workerId)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onDetach = async (workerId: number) => {
	try {
		isLoading.value = true
		await store.detachWorkerFromProject(workerId)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onChangeWorkerStage = async (stage: boolean, workerId: number) => {
	if (stage) {
		await onAttach(workerId)
	} else {
		await onDetach(workerId)
	}
}

const onChangeWorkerStageFromName = async (workerId: number) => {
	if (isWorkerAdded(workerId)) {
		await onDetach(workerId)
	} else {
		await onAttach(workerId)
	}
}

onMounted(async () => {
	await onFetchWorkers()
	await nextTick(() => $input.value?.focusInput())
})
</script>

<style scoped lang="scss">
.add-worker-modal {
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	padding: 12px 0 0 12px;

	&__search {
		display: flex;
		gap: 8px;
		width: 100%;
		margin-bottom: 8px;
		padding-right: 12px;

		&-input {
			width: 100%;
		}
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
		padding-right: 12px;
		color: #979797;

		&-title {
			font-size: 12px;
			line-height: 16px;
			font-weight: 600;
		}
	}

	&__body {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 8px;
		overflow: auto;
		padding-right: 12px;
		padding-bottom: 12px;

		&-department {
			display: flex;
			flex-direction: column;
			gap: 8px;

			&-title {
				font-size: 12px;
				line-height: 16px;
				font-weight: 600;
				color: #979797;
			}
		}

		&-item {
			display: flex;
			font-size: 14px;
			line-height: 18px;
			color: #333;
			cursor: pointer;
			transition: color 0.1s;

			&--nested {
				padding-left: 16px;
			}

			&-text {
				font-size: 14px;
				line-height: 18px;
			}

			&:hover {
				text-decoration: underline;
				color: #396742;
			}
		}
	}
}
</style>
